/**
 * ReprintLabelDialog
 *
 * Confirmation dialog for reprinting a single garment label.
 * Fetches label data, renders GarmentLabel in hidden ref, prints via
 * usePrintLabels, and logs the reprint event for audit.
 *
 * Flow:
 * 1. Dialog opens → fetches garment label data in background
 * 2. User selects reason → clicks "Reprint Label"
 * 3. GarmentLabel rendered in hidden div → browser print dialog opens
 * 4. After print, logPrintEvent mutation fires with reason + garmentUnitId
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { GarmentLabel } from '@/features/production-orders/components/labels/GarmentLabel';
import { usePrintLabels } from '@/features/production-orders/hooks/usePrintLabels';
import { useDefaultLabelSize } from '@/features/production-orders/hooks/useLabelTemplate';

type ReprintReason = 'damaged' | 'lost' | 'printer_error' | 'other';

const REASON_OPTIONS: Array<{ value: ReprintReason; labelKey: string }> = [
  { value: 'damaged', labelKey: 'tracker.reprint.reasons.damaged' },
  { value: 'lost', labelKey: 'tracker.reprint.reasons.lost' },
  { value: 'printer_error', labelKey: 'tracker.reprint.reasons.printerError' },
  { value: 'other', labelKey: 'tracker.reprint.reasons.other' },
];

interface ReprintLabelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garmentId: string;
  garmentSku: string;
  garmentStatus?: string;
}

export function ReprintLabelDialog({
  open,
  onOpenChange,
  garmentId,
  garmentSku,
  garmentStatus,
}: ReprintLabelDialogProps) {
  const { t } = useTranslation('production');
  const isFirstPrint = garmentStatus === 'PENDING';
  const [reason, setReason] = useState<ReprintReason | ''>('');
  const [hasTriggeredPrint, setHasTriggeredPrint] = useState(false);

  const { defaultLabelSize } = useDefaultLabelSize();

  // Fetch label data when dialog opens
  const { data: labelData, isLoading: labelLoading } =
    trpc.productionOrders.getGarmentLabel.useQuery(
      { garmentUnitId: garmentId },
      { enabled: open }
    );

  const logPrintEvent = trpc.productionOrders.logPrintEvent.useMutation();

  const { printRef, printLabels, isPrinting } = usePrintLabels({
    showSuccessToast: false,
    onPrintComplete: () => {
      if (!reason) return;
      // Log the reprint event for audit
      logPrintEvent.mutate(
        {
          garmentUnitId: garmentId,
          printType: 'LABEL_REPRINT',
          reason: reason as 'damaged' | 'lost' | 'printer_error' | 'other',
        },
        {
          onSuccess: () => {
            toast.success(t('tracker.reprint.sentToPrinter'), {
              description: t('tracker.reprint.reprintLogged', { sku: garmentSku }),
            });
          },
          onError: () => {
            // Print already happened, just warn about logging
            toast.warning(t('tracker.reprint.loggingFailed'), {
              description: t('tracker.reprint.loggingFailedDesc'),
            });
          },
        }
      );
      onOpenChange(false);
    },
  });

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setReason('');
      setHasTriggeredPrint(false);
    }
  }, [open]);

  // Trigger print once label data is ready and user has confirmed
  useEffect(() => {
    if (!hasTriggeredPrint || !labelData || !printRef.current) return;
    // Small delay to ensure GarmentLabel has rendered into the hidden div
    const timer = setTimeout(() => {
      printLabels('', defaultLabelSize, `Label-Reprint-${garmentSku}`);
    }, 100);
    return () => clearTimeout(timer);
  }, [hasTriggeredPrint, labelData, printLabels, defaultLabelSize, garmentSku, printRef]);

  const handleConfirm = () => {
    if (!reason) return;
    setHasTriggeredPrint(true);
  };

  const isReady = !!labelData && !!reason;
  const isWorking = isPrinting || logPrintEvent.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            {isFirstPrint ? t('tracker.reprint.printLabel') : t('tracker.reprint.reprintLabel')}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {t('tracker.reprint.printDescription', { sku: garmentSku })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label htmlFor="reprint-reason">{t('tracker.reprint.reasonLabel')}</Label>
            <Select
              value={reason}
              onValueChange={(v) => setReason(v as ReprintReason)}
            >
              <SelectTrigger id="reprint-reason">
                <SelectValue placeholder={t('tracker.reprint.selectReason')} />
              </SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            {t('tracker.reprint.auditNote')}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isWorking}
          >
            {t('tracker.reprint.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isReady || isWorking || labelLoading}
          >
            <Printer className="h-4 w-4 mr-2" />
            {labelLoading ? t('tracker.reprint.loading') : isWorking ? t('tracker.reprint.printing') : isFirstPrint ? t('tracker.reprint.printBtn') : t('tracker.reprint.reprintBtn')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Hidden label render target — usePrintLabels reads innerHTML from this ref */}
      <div ref={printRef} style={{ display: 'none' }}>
        {labelData && (
          <GarmentLabel
            garment={{
              id: labelData.id,
              orderNumber: labelData.orderNumber,
              sku: labelData.sku,
              shopifySku: labelData.shopifySku,
              productName: labelData.productName,
              size: labelData.size,
              color: labelData.color,
              requiresPretreat: labelData.requiresPretreat,
              fulfillmentType: labelData.fulfillmentType,
              itemNumber: labelData.itemNumber,
              totalItems: labelData.totalItems,
              shopifyProductType: labelData.shopifyProductType,
              // BLU-233: real per-garment tech → reprint label QR m + DTF/DTG indicator.
              printTechnology: labelData.printTechnology,
              // Phase 104 (D-09): manual order print areas ([] otherwise).
              printArea: labelData.printArea,
            }}
            binLabel={labelData.binLabel ?? undefined}
            isRedo={labelData.isRedo}
            redoReason={labelData.redoReason ?? undefined}
          />
        )}
      </div>
    </Dialog>
  );
}
